import React, { useEffect, useState } from 'react';
import { Spinner, SpinnerSize, mergeStyleSets } from '@fluentui/react';
import { ArrowLeft, SquaresFour, Trash } from '@phosphor-icons/react';
import { dashboardBuilderService } from '../../services/dashboardBuilderService';

interface DashboardSummary {
  id: string;
  name: string;
  datasetIds: string[];
  widgetCount: number;
  createdAt: string;
  updatedAt: string;
}

interface Props {
  onOpen: (id: string) => void;
  onBack: () => void;
}

const s = mergeStyleSets({
  root: {
    maxWidth: 800,
    margin: '0 auto',
    padding: '40px 24px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
  },
  backBtn: {
    background: 'none',
    border: '1px solid #E1E1E1',
    borderRadius: 4,
    width: 32,
    height: 32,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#484848',
    selectors: { ':hover': { background: '#F5F5F5' } },
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#232323',
    flex: 1,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: 14,
  },
  card: {
    borderRadius: 8,
    padding: 18,
    background: '#FFFFFF',
    border: '1px solid #E1E1E1',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    selectors: {
      ':hover': {
        borderColor: '#2e873d',
        boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
        transform: 'translateY(-1px)',
      },
    },
  },
  cardName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#232323',
    marginBottom: 6,
  },
  cardMeta: {
    fontSize: 12,
    color: '#747474',
    marginBottom: 4,
  },
  cardActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 10,
  },
  openBtn: {
    background: '#2e873d',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 4,
    padding: '4px 14px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    selectors: { ':hover': { background: '#256d31' } },
  },
  deleteBtn: {
    background: 'none',
    border: 'none',
    color: '#747474',
    cursor: 'pointer',
    fontSize: 12,
    selectors: { ':hover': { color: '#D13438' } },
  },
  empty: {
    textAlign: 'center' as const,
    padding: '60px 0',
    color: '#747474',
    fontSize: 14,
  },
});

export default function SavedDashboardsList({ onOpen, onBack }: Props) {
  const [dashboards, setDashboards] = useState<DashboardSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const list = await dashboardBuilderService.listDashboards();
      setDashboards(list);
    } catch (err) {
      console.error('Failed to load saved dashboards:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!window.confirm('Delete this dashboard?')) return;
    try {
      await dashboardBuilderService.deleteDashboard(id);
      setDashboards((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      console.error('Failed to delete dashboard:', err);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Spinner size={SpinnerSize.large} label="Loading dashboards..." />
      </div>
    );
  }

  return (
    <div className={s.root}>
      <div className={s.header}>
        <button className={s.backBtn} onClick={onBack} title="Back">
          <ArrowLeft size={14} weight="regular" />
        </button>
        <span className={s.title}>Saved Dashboards</span>
      </div>

      {dashboards.length === 0 ? (
        <div className={s.empty}>
          <SquaresFour size={36} color="#D0D0D0" weight="regular" style={{ display: 'block', marginBottom: 12 }} />
          No saved dashboards yet. Build your first one!
        </div>
      ) : (
        <div className={s.grid}>
          {dashboards.map((d) => (
            <div key={d.id} className={s.card} onClick={() => onOpen(d.id)}>
              <div className={s.cardName}>{d.name}</div>
              <div className={s.cardMeta}>
                {d.widgetCount} widget{d.widgetCount !== 1 ? 's' : ''}
                {' \u00b7 '}
                {d.datasetIds.join(', ')}
              </div>
              <div className={s.cardMeta}>
                Updated {new Date(d.updatedAt).toLocaleDateString()}
              </div>
              <div className={s.cardActions}>
                <button className={s.openBtn} onClick={() => onOpen(d.id)}>Open</button>
                <button className={s.deleteBtn} onClick={(e) => handleDelete(e, d.id)}>
                  <Trash size={13} weight="regular" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
